// TypingActionAreaRedux.tsx - INTEGRATION with GenericConfirmationModal

import React, { useState, ChangeEvent, KeyboardEvent, useEffect, useCallback, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { IoMdAttach } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { ReplyIcon, Upload, FileText, Image as ImageIcon, Video, X, Paperclip } from "lucide-react";
import { Spin } from "antd";
import toast from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import logger, { Logger } from '../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import type { RootState } from "../../store/store";
import {
  selectSelectedConversation
} from "../../store/slices/conversationsSlice";
import {
  selectReplyContext,
  clearReplyContext,
  setReplyContext,
  sendMessage,
  sendTemplate,  // 🔥 Import sendTemplate
  selectLead,
  addOptimisticMessage
} from "../../store/slices/messagesSlice";
import { selectWhatsappPhoneId } from "../../store/slices/userSlice";
import { setLoadingState, selectLoadingState } from "../../store/slices/uiSlice";
// 🔥 NEW: Import content slice for slash commands
import {
  fetchAllContent,
  selectAllContent,
  selectContentLoading,
  type ContentItem
} from "../../store/slices/contentSlice";

// Import WhatsApp Content Selector
import WhatsAppContentSelector from "./WhatsAppContentSelector";

// Import media validation constants
import {
  DOC_SIZE_LIMIT,
  IMAGE_SIZE_LIMIT,
  VIDEO_SIZE_LIMIT
} from "../template/createTemplate/headerSection/whatsapp-media-validator";
import { isImage, isVideo } from "../../utils/helpers";

// 🔥 IMPORT: Updated multi-file upload hook and UI components
import { useMultiFileUpload } from './multiFileUpload';
import { MultiFileQueue, DragOverlay, FileCountBadge } from './MultiFileUI';

// 🔥 IMPORT: GenericConfirmationModal for multiple files confirmation
import GenericConfirmationModal from "../../components/GenericConfirmationModal";

// 🔥 IMPORT: ProductPickerPopup for sharing products
import ProductPickerPopup from "../../components/ProductPickerPopup";
import type { Product } from "../../components/ProductPickerPopup";

// 🔥 NEW: Import SlashCommandDropdown for "/" command
import SlashCommandDropdown from "./SlashCommandDropdown";

// Type definitions
interface TypingActionAreaReduxProps {
  scrollToBottom?: () => void;
  onAddFilesReady?: (addFiles: (files: File[]) => void) => void;  // Callback to expose addFiles to parent
}

// Main component
const TypingActionAreaRedux: React.FC<TypingActionAreaReduxProps> = ({ scrollToBottom, onAddFilesReady }) => {
  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const replyContext = useAppSelector(selectReplyContext);
  const phoneId = useAppSelector(selectWhatsappPhoneId);
  const isLoading = useAppSelector(selectLoadingState('sendMessage'));
  const currentLead = useAppSelector(selectLead);
  const userState = useAppSelector((state: RootState) => state.rootReducers.userData.userDetails);

  // Local state
  const [reply, setReply] = useState<string>("");
  const [showContentSelector, setShowContentSelector] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);  // 🔥 Track selected template

  // 🔥 NEW: Product picker state
  const [isProductPickerOpen, setIsProductPickerOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

  // 🔥 NEW: Slash command state
  const [showSlashDropdown, setShowSlashDropdown] = useState(false);
  const [slashSearchTerm, setSlashSearchTerm] = useState('');
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);

  // 🔥 Redux content for slash commands
  const allContent = useAppSelector(selectAllContent);
  const contentLoading = useAppSelector(selectContentLoading);

  // 🔥 UPDATED: Destructure confirmationModal from the hook
  const {
    files,
    uploading,
    addFiles,
    removeFile,
    setConversation,
    queueCount,
    completedCount,
    confirmationModal  // ← ADD: Get the confirmation modal props
  } = useMultiFileUpload();

  // Ref for textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 🔥 Expose addFiles to parent component
  useEffect(() => {
    if (onAddFilesReady) {
      onAddFilesReady(addFiles);
    }
  }, [addFiles, onAddFilesReady]);

  // 🔥 NEW: Fetch content from Redux when slash command is triggered
  useEffect(() => {
    if (showSlashDropdown && allContent.length === 0 && !contentLoading.content) {
      logger.log('Slash command triggered - fetching content from Redux');
      dispatch(fetchAllContent({}));
    }
  }, [showSlashDropdown, allContent.length, contentLoading.content, dispatch]);

  // Prepare lead object for content selector
  const leadForSelector = currentLead || {
    _id: selectedConversation?._id || '',
    name: selectedConversation?.name || '',
    phone: selectedConversation?.mobile || ''
  };

  // Debug logging for component lifecycle and props
  useEffect(() => {
    Logger.log("TypingActionAreaRedux rendered", {
      selectedConversation: selectedConversation?.id,
      replyContext: replyContext?._id,
      phoneId
    });
  }, [selectedConversation, replyContext, phoneId]);

  // Setup conversation for multi-file upload
  useEffect(() => {
    if (selectedConversation && phoneId) {
      setConversation(selectedConversation, phoneId);
      Logger.log("Multi-file conversation set", {
        id: selectedConversation.id,
        mobile: selectedConversation.mobile
      });
    }
  }, [selectedConversation, phoneId, setConversation]);

  // Focus textarea when reply context changes
  useEffect(() => {
    if (replyContext && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyContext]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [reply]);

  // Auto-scroll when files complete
  useEffect(() => {
    if (completedCount > 0 && scrollToBottom) {
      setTimeout(scrollToBottom, 300);
    }
  }, [completedCount, scrollToBottom]);

  // Extract message content from message object
  const getMessageContent = (msg: any): string => {
    if (!msg || !msg.message) return '';
    
    const message = msg.message;
    
    if (typeof message === 'string') return message;
    if (typeof message.body === 'string') return message.body;
    if (message.body?.text?.body) return message.body.text.body;
    if (message.text?.body) return message.text.body;
    
    try {
      return JSON.stringify(message);
    } catch (e) {
      return '';
    }
  };

  // File validation helper
  const validateFile = (file: File): boolean => {
    if (isImage(file.name) && file.size > IMAGE_SIZE_LIMIT) {
      toast.error("Image must be less than 5 MB");
      return false;
    }

    if (isVideo(file.name) && file.size > VIDEO_SIZE_LIMIT) {
      toast.error("Video must be less than 16 MB");
      return false;
    }

    if (!isImage(file.name) && !isVideo(file.name) && file.size > DOC_SIZE_LIMIT) {
      toast.error("Document must be less than 16 MB");
      return false;
    }

    return true;
  };

  // 🔥 UPDATED: Multi-file dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      Logger.log('📂 Multi-file drop detected', { 
        accepted: acceptedFiles.length, 
        rejected: rejectedFiles.length 
      });

      // Log rejected files
      rejectedFiles.forEach(rejection => {
        Logger.log('❌ File rejected:', rejection.file.name, rejection.errors);
      });

      // Add accepted files to queue
      if (acceptedFiles.length > 0) {
        // 📢 IMPORTANT: This will trigger confirmation modal for multiple files
        addFiles(acceptedFiles);
        
        if (acceptedFiles.length > 1) {
          logger.log(`🔔 MULTIPLE FILES DETECTED: ${acceptedFiles.length} files - confirmation modal will appear`);
        } else {
          logger.log(`📤 SINGLE FILE: ${acceptedFiles[0].name} - will upload directly`);
        }
      }
    },
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'video/mp4': ['.mp4'],
      'video/3gpp': ['.3gp'],
      'application/pdf': ['.pdf']
    },
    multiple: true,        // Enable multiple files
    noClick: true,
    noKeyboard: true,
    disabled: false
  });

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get file type for icon display
  const getFileType = (fileName: string): 'image' | 'video' | 'document' => {
    if (isImage(fileName)) return 'image';
    if (isVideo(fileName)) return 'video';
    return 'document';
  };

  // 🔥 NEW: Handle product selection from ProductPickerPopup
  const handleProductsSelect = useCallback((products: any[]) => {
    Logger.log('📦 Products selected in WhatsApp:', products);

    // Store selected products
    setSelectedProducts(products);

    // Close the product picker
    setIsProductPickerOpen(false);

    // Note: The actual sharing happens via the "Share Catalogue" button
    // inside the ProductPickerPopup component
    toast.success(`${products.length} product(s) selected`);
  }, []);

  // 🔥 NEW: Filter content based on slash search term (search by title only)
  const filteredSlashContent = useMemo(() => {
    if (!showSlashDropdown) return [];

    // Only show message-type content for slash commands
    let filtered = allContent.filter(item => item.type === 'message');

    // Filter by search term (titles only)
    if (slashSearchTerm.trim()) {
      const searchTerm = slashSearchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const title = item.name || item.details?.title || '';
        return title.toLowerCase().includes(searchTerm);
      });
    }

    return filtered;
  }, [allContent, slashSearchTerm, showSlashDropdown]);

  // 🔥 NEW: Reset selected index when filtered content changes
  useEffect(() => {
    setSlashSelectedIndex(0);
  }, [filteredSlashContent.length]);

  // 🔥 NEW: Handle slash command content selection
  const handleSlashContentSelect = useCallback((content: ContentItem) => {
    logger.log('✅ Slash command content selected:', content._id);

    // Get message content
    const messageContent = content.content ||
                          content.details?.message ||
                          content.details?.description ||
                          content.details?.title ||
                          '';

    if (!messageContent) {
      toast.error('No message content available');
      return;
    }

    // Personalize message with lead info
    const leadName = currentLead?.name || selectedConversation?.name || 'Customer';
    const personalizedMessage = messageContent
      .replace(/\{\{name\}\}/gi, leadName)
      .replace(/\{\{phone\}\}/gi, currentLead?.phone || selectedConversation?.mobile || '');

    // Replace the "/" command with the content
    setReply(personalizedMessage);

    // Close dropdown
    setShowSlashDropdown(false);
    setSlashSearchTerm('');
    setSlashSelectedIndex(0);

    // Focus textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }

    logger.log('✨ Message inserted via slash command');
  }, [currentLead, selectedConversation]);

  // Send message function
  const sendMessageHandler = async () => {
    if (!selectedConversation) {
      Logger.log("Cannot send message - no selected conversation");
      return;
    }

    if (!reply.trim() && queueCount === 0) {
      Logger.log("Cannot send message - no content to send");
      return;
    }

    // 🔥 Check if template is selected
    if (selectedTemplateId) {
      Logger.log("📋 Sending WhatsApp template:", selectedTemplateId);

      // Clear template selection and input
      setSelectedTemplateId(null);
      setReply("");
      dispatch(clearReplyContext());

      // Send template
      dispatch(sendTemplate({
        phone: selectedConversation.mobile,
        templateId: selectedTemplateId,
        phoneId: phoneId || ''
      }));

      Logger.log("📤 Template send dispatched");

      if (scrollToBottom) {
        setTimeout(scrollToBottom, 100);
      }
      return;
    }

    // 🔥 OPTIMISTIC UI: Store message text and clear input immediately
    const messageText = reply.trim();
    const currentReplyContext = replyContext;

    if (messageText) {
      // Get user ID for optimistic message
      const userId = typeof userState === 'undefined' ? 'current-user' :
        Array.isArray(userState) ?
          (userState.length > 0 && userState[0] && typeof userState[0] === 'object' ?
            (userState[0] as {_id?: string})._id || 'current-user' : 'current-user') :
          (userState && typeof userState === 'object' ?
            (userState as {_id?: string})._id || 'current-user' : 'current-user');

      // 🔥 FIXED: Use messageId (WhatsApp ID) instead of _id (MongoDB ID) for reply context
      const whatsappMessageId = currentReplyContext?.messageId || currentReplyContext?._id;

      // 🚀 Add optimistic message immediately
      dispatch(addOptimisticMessage({
        text: messageText,
        phone: selectedConversation.mobile,
        replyToMessageId: whatsappMessageId,
        userId: userId
      }));

      // Clear input immediately (optimistic UI)
      setReply("");

      // Clear reply context immediately
      if (currentReplyContext) {
        dispatch(clearReplyContext());
      }

      Logger.log("💨 OPTIMISTIC UI: Message added to chat, input cleared", {
        text: messageText.substring(0, 50) + '...',
        replyToMessageId: whatsappMessageId,
        phone: selectedConversation.mobile
      });

      // Trigger API call in background (non-blocking)
      dispatch(sendMessage({
        phone: selectedConversation.mobile,
        text: messageText,
        replyToMessageId: whatsappMessageId,
        phoneId: phoneId || ''
      }));

      Logger.log("📤 API call dispatched in background with WhatsApp messageId");
    }
    
    if (scrollToBottom) {
      setTimeout(scrollToBottom, 100);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // 🔥 NEW: Handle slash dropdown navigation
    if (showSlashDropdown && filteredSlashContent.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSlashSelectedIndex(prev =>
            prev < filteredSlashContent.length - 1 ? prev + 1 : prev
          );
          return;

        case 'ArrowUp':
          e.preventDefault();
          setSlashSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
          return;

        case 'Enter':
          e.preventDefault();
          if (filteredSlashContent[slashSelectedIndex]) {
            handleSlashContentSelect(filteredSlashContent[slashSelectedIndex]);
          }
          return;

        case 'Escape':
          e.preventDefault();
          setShowSlashDropdown(false);
          setSlashSearchTerm('');
          setSlashSelectedIndex(0);
          return;

        default:
          break;
      }
    }

    // Normal Enter key behavior (send message)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessageHandler();
    }
  };

  // 🔥 UPDATED: Multi-file input handler
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    Logger.log("Files selected via input", { 
      fileCount: selectedFiles.length,
      fileNames: selectedFiles.map(f => f.name)
    });

    if (selectedFiles.length > 0) {
      // 📢 IMPORTANT: This will trigger confirmation modal for multiple files
      addFiles(selectedFiles);
      
      if (selectedFiles.length > 1) {
        logger.log(`🔔 MULTIPLE FILES SELECTED: ${selectedFiles.length} files - confirmation modal will appear`);
      } else {
        logger.log(`📤 SINGLE FILE SELECTED: ${selectedFiles[0].name} - will upload directly`);
      }
    }
    
    // Reset input value
    e.target.value = '';
  };

  // Render reply preview
  const renderReplyPreview = () => {
    if (!replyContext) return null;

    const messageContent = getMessageContent(replyContext);
    
    return (
      <div style={{
        background: 'linear-gradient(90deg, #25d366 0%, #128c7e 100%)',
        borderRadius: '12px',
        padding: '12px',
        margin: '8px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(37, 211, 102, 0.2)'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '50%',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}>
          <ReplyIcon size={16} />
        </div>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: 'white',
            fontWeight: '600',
            fontSize: '12px',
            marginBottom: '2px',
            opacity: 0.9
          }}>
            Replying to {replyContext.sender ? 'You' : selectedConversation?.name}
          </div>
          <div style={{
            color: 'white',
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            opacity: 0.8
          }}>
            {messageContent}
          </div>
        </div>
        
        <button
          onClick={() => dispatch(clearReplyContext())}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
          }}
        >
          <X size={14} />
        </button>
      </div>
    );
  };

  // Main component styles
  const containerStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '80px',
    maxHeight: '250px',
    padding: '20px 24px',
    background: '#ffffff',
    borderTop: '1px solid #e9ecef',
    boxShadow: '0 -4px 16px rgba(0,0,0,0.04), 0 -1px 4px rgba(0,0,0,0.02)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const inputContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
    background: '#f8f9fa',
    borderRadius: '16px',
    border: `1px solid ${reply.trim() ? '#0084ff' : '#e9ecef'}`,
    padding: '12px 16px',
    minHeight: '56px',
    boxShadow: reply.trim() ? '0 0 0 3px rgba(0, 132, 255, 0.08)' : 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const textareaStyle: React.CSSProperties = {
    flex: 1,
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: '15px',
    lineHeight: '1.5',
    padding: '4px 0',
    backgroundColor: 'transparent',
    minHeight: '28px',
    maxHeight: '120px',
    overflow: 'auto',
    fontFamily: 'Gilroy-Medium',
    color: '#1a1a1a'
  };

  const buttonStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: '600',
    padding: '6px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none',
    whiteSpace: 'nowrap'
  };

  const smallButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: '#ffffff',
    color: '#495057',
    border: '1px solid #e9ecef',
    minWidth: '80px',
    height: '32px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
  };

  // Determine if button should be enabled
  const hasContent = reply.trim() || selectedTemplateId;
  const isButtonDisabled = !hasContent || isLoading;

  const sendButtonStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '12px',
    width: 'auto',
    minWidth: '130px',  // Increased to accommodate "Send Template" text
    height: '44px',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none',
    fontSize: '14px',
    fontWeight: '700',
    background: isLoading
      ? 'linear-gradient(135deg, #e9ecef, #dee2e6)'
      : isButtonDisabled
        ? 'linear-gradient(135deg, #e9ecef, #dee2e6)'
        : selectedTemplateId
          ? 'linear-gradient(135deg, #10b981, #059669)'  // Green for template
          : 'linear-gradient(135deg, #0084ff, #0066cc)',  // Blue for normal send
    color: isButtonDisabled ? '#6c757d' : 'white',
    cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
    boxShadow: isButtonDisabled
      ? 'none'
      : selectedTemplateId
        ? '0 4px 12px rgba(16, 185, 129, 0.25)'
        : '0 4px 12px rgba(0, 132, 255, 0.25)'
  };

  return (
    <div {...getRootProps({ className: 'dropzone-container' })} style={{ width: '100%', position: 'relative', zIndex: 100 }}>
      <input {...getInputProps()} />

      {/* Multi-file drag overlay */}
      <DragOverlay isDragActive={isDragActive} />

      {/* 🔥 NEW: Slash Command Dropdown - Positioned via Portal */}
      <SlashCommandDropdown
        isOpen={showSlashDropdown}
        filteredContent={filteredSlashContent}
        selectedIndex={slashSelectedIndex}
        searchTerm={slashSearchTerm}
        onSelect={handleSlashContentSelect}
        onClose={() => {
          setShowSlashDropdown(false);
          setSlashSearchTerm('');
          setSlashSelectedIndex(0);
        }}
        loading={contentLoading.content}
        anchorElement={textareaRef.current} // Pass textarea ref for positioning
      />

      <div style={containerStyle}>
        {/* Multi-file queue UI */}
        <MultiFileQueue
          files={files}
          uploading={uploading}
          onRemove={removeFile}
          onClearCompleted={() => {
            const completedFiles = files.filter(f => f.status === 'completed');
            completedFiles.forEach(file => removeFile(file.id));
          }}
        />
        
        {renderReplyPreview()}
        
        <div style={inputContainerStyle}>
          <textarea
            ref={textareaRef}
            placeholder="Type a message or use / for quick content..."
            value={reply}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              const newValue = e.target.value;
              setReply(newValue);

              // 🔥 NEW: Detect slash command
              if (newValue.startsWith('/') && !showSlashDropdown) {
                logger.log('Slash command detected - opening dropdown');
                setShowSlashDropdown(true);
                setSlashSearchTerm(newValue.slice(1)); // Remove the "/"
              } else if (newValue.startsWith('/') && showSlashDropdown) {
                // Update search term as user types after "/"
                setSlashSearchTerm(newValue.slice(1));
              } else if (!newValue.startsWith('/') && showSlashDropdown) {
                // Close dropdown if "/" is removed
                setShowSlashDropdown(false);
                setSlashSearchTerm('');
                setSlashSelectedIndex(0);
              }

              // 🔥 Clear template selection when user starts typing manually
              if (selectedTemplateId && newValue.trim()) {
                setSelectedTemplateId(null);
                Logger.log("📋 Template cleared - user is typing manually");
              }
            }}
            onKeyDown={handleKeyDown}
            style={textareaStyle}
          />
          
          {/* All buttons in horizontal row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setShowContentSelector(true)}
              style={smallButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef, #dee2e6)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa, #e9ecef)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              title="Select content from templates"
            >
              + Content
            </button>

            {/* 🔥 NEW: Share Products button */}
            <button
              onClick={() => setIsProductPickerOpen(true)}
              style={{
                ...smallButtonStyle,
                background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                color: '#16a34a',
                border: '1px solid #86efac',
                fontWeight: '600'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #dcfce7, #bbf7d0)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(22, 163, 74, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4, #dcfce7)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.04)';
              }}
              title="Share products catalogue with customer"
            >
              📦 Products
            </button>

            {/* File input for traditional file upload */}
            <label htmlFor="fileInput">
              <div
                style={{
                  ...smallButtonStyle,
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #e9ecef, #dee2e6)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa, #e9ecef)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                title="Upload files (supports multiple files)"
              >
                + Files
                {/* File count badge */}
                {queueCount > 0 && (
                  <FileCountBadge count={queueCount} uploading={uploading} />
                )}
              </div>
            </label>
            <input
              accept=".jpeg,.png,.jpg,.mp4,.3gp,.pdf"
              type="file"
              id="fileInput"
              multiple                    // Enable multiple files
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            
            {/* Send button */}
            <button
              onClick={sendMessageHandler}
              disabled={isButtonDisabled}
              style={sendButtonStyle}
              onMouseEnter={(e) => {
                if (!isButtonDisabled) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  if (selectedTemplateId) {
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.35)';
                  } else {
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 132, 255, 0.35)';
                  }
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                if (selectedTemplateId) {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.25)';
                } else {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 132, 255, 0.25)';
                }
              }}
              title={
                isLoading
                  ? "Sending..."
                  : selectedTemplateId
                    ? "Send WhatsApp Template"
                    : !reply.trim()
                      ? "Type a message or select a template"
                      : "Send message"
              }
            >
              {isLoading ? (
                <>
                  <Spin size="small" />
                  <span>Sending</span>
                </>
              ) : selectedTemplateId ? (
                <>
                  <span>📋</span>
                  <span>Send Template</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} size="sm" />
                  <span>Send</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* WhatsApp Content Selector */}
      <WhatsAppContentSelector
  isOpen={showContentSelector}
  onClose={() => setShowContentSelector(false)}
  onContentSelect={(processedMessage: React.SetStateAction<string>, templateId?: string) => {
    logger.log('📨 Content selected for typing area:', { processedMessage, templateId });

    if (reply.trim()) {
      Logger.log("Content selector replacing existing text:", reply, "with:", processedMessage);
    }

    // 🔥 If template is selected, store template ID
    if (templateId) {
      setSelectedTemplateId(templateId);
      Logger.log("📋 Template selected:", templateId);
    } else {
      setSelectedTemplateId(null);
    }

    setReply(processedMessage);
    setShowContentSelector(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }}
  currentLead={leadForSelector}
  addFiles={addFiles}
/>

      {/* 🔥 NEW: GenericConfirmationModal for multiple files */}
      <GenericConfirmationModal
        showModal={confirmationModal.showModal}
        onConfirmation={confirmationModal.onConfirmation}
        toggleModal={confirmationModal.toggleModal}
        message={confirmationModal.message}
        title={confirmationModal.title}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        isWarning={false}  // File upload confirmation is not a warning
        style={{ zIndex: 10000 }}  // Ensure modal appears on top
      />

      {/* 🔥 NEW: ProductPickerPopup for sharing products */}
      {isProductPickerOpen && (
        <ProductPickerPopup
          isOpen={isProductPickerOpen}
          onClose={() => setIsProductPickerOpen(false)}
          onProductsSelect={handleProductsSelect}
          initialSelectedProducts={selectedProducts}
          leadId={currentLead?._id || selectedConversation?._id}
          leadName={currentLead?.name || selectedConversation?.name}
          source="lead"  // Use 'lead' source to enable Share Catalogue and Create Quotation buttons
        />
      )}
    </div>
  );
};

export default TypingActionAreaRedux;